import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EMPTY, catchError, forkJoin, map, Observable, of, switchMap, tap } from 'rxjs';
import { Friendship, ItemTypeConfig, User, UserImage, UserProfile } from 'src/app/dto';
import { AbstractService } from '../abstractservice';
import { AuthService } from '../auth.service';
import { FeatureFlagService } from '../feature-flag.service';
import { Cacheable, Hydratable, JsonEnricher, JsonMappable } from '../jsonmapper.service';
import { CacheRepository, CacheResult } from './cache/cache-repository';
import { AbstractRemoteRepository } from './remote/abstract-remote-repository';
import { FriendshipRepositoryService } from './remote/friendship-repository.service';
import { ItemTypeRepositoryService } from './remote/item-type-repository.service';
import { UserImageRepositoryService } from './remote/user-image-repository.service';
import { UserProfileRepositoryService } from './remote/user-profile-repository.service';
import { UserRepositoryService } from './remote/user-repository.service';

@Injectable({
    providedIn: 'root'
})
export class UnifiedRepositoryGatewayService extends AbstractService {

    public jsonMapper: JsonEnricher;

    private userCache: CacheRepository<string, User>;
    private itemTypeCache: CacheRepository<number, ItemTypeConfig>;
    private userProfileCache: CacheRepository<string, UserProfile>;
    private userImageCache: CacheRepository<string, UserImage>;
    private repositoryRegistrations: Map<new () => any, RepositoryRegistration<any>>;


    constructor(auth: AuthService,
        featureFlagService: FeatureFlagService,
        private http: HttpClient,
        public userRepository: UserRepositoryService,
        public itemTypeRepository: ItemTypeRepositoryService,
        public userProfileRepository: UserProfileRepositoryService,
        public friendshipRepository: FriendshipRepositoryService,
        public userImageRepository: UserImageRepositoryService) {
        super(auth, featureFlagService);
        this.jsonMapper = new JsonEnricher(this);

        this.userCache = new CacheRepository<string, User>(user => user.Username, http, auth, featureFlagService, User);
        this.itemTypeCache = new CacheRepository<number, ItemTypeConfig>(itemType => itemType.Id, http, auth, featureFlagService, ItemTypeConfig);
        this.userProfileCache = new CacheRepository<string, UserProfile>(userProfile => userProfile.Username, http, auth, featureFlagService, UserProfile);
        this.userImageCache = new CacheRepository<string, UserImage>(userImage => userImage.Id, http, auth, featureFlagService, UserImage);
        this.repositoryRegistrations = new Map<new () => any, RepositoryRegistration<any>>([
            [ItemTypeConfig, { remoteRepository: this.itemTypeRepository, cacheRepository: this.itemTypeCache }],
            [User, { remoteRepository: this.userRepository, cacheRepository: this.userCache }],
            [UserProfile, { remoteRepository: this.userProfileRepository, cacheRepository: this.userProfileCache }],
            [Friendship, { remoteRepository: this.friendshipRepository }],
            [UserImage, { remoteRepository: this.userImageRepository, cacheRepository: this.userImageCache }],
        ]);
    }

    /** inadvisable! */
    public retrieveWithNoId<TCacheable>(withFullHydration: boolean, withFullMapping: boolean, type: new () => TCacheable): Observable<TCacheable[]> {
        var remoteRepo: AbstractRemoteRepository<any, any> = this.getRemoteRepository(type);
        var pipeFn = withFullHydration && this.isHydratableType(type) ?
            switchMap((items: TCacheable[]) => this.hydrateItems(type, ...items)) :
            (withFullMapping && this.isJsonMappableType(type) ?
                map((items: TCacheable[]) => this.mapItems(type, ...items)) :
                map((item: TCacheable[]) => item));

        return remoteRepo.retrieveBulkHttpCall(null)
            .pipe(pipeFn);

    }

    // Coalesces concurrent lookups for the same id: two calls for the same not-yet-cached id,
    // arriving before either resolves, now share one HTTP call and land the exact same object
    // instance rather than each firing their own fetch and getting their own copy. Handles both
    // the single-id and multi-id shape uniformly - a caller asking for N ids may find some
    // already in flight (attach to them) and others
    // genuinely new (one combined fetch for just those). Types with no cache repository
    // (Hydratable-but-never-cached types like Friendship) have nowhere to track in-flight
    // requests and fall back to the old, uncoalesced behavior - see the early-return branch.
    public retrieve<TEntity>(withFullHydration: boolean, withFullMapping: boolean, type: new () => TEntity, ...ids: any[]): Observable<TEntity[]> {
        if (ids === null || ids === undefined || ids.length === 0) return of([]);
        ids = ids.filter((item, index, arr) => arr.indexOf(item) === index); //dedupe

        var remoteRepo: AbstractRemoteRepository<any, any> = this.getRemoteRepository(type);
        var pipeFn = withFullHydration && this.isHydratableType(type) ?
            switchMap((items: TEntity[]) => this.hydrateItems(type, ...items)) :
            (withFullMapping && this.isJsonMappableType(type) ?
                map((items: TEntity[]) => this.mapItems(type, ...items)) :
                map((items: TEntity[]) => items));
        var fetchIds = (idsToFetch: any[]): Observable<TEntity[]> =>
            (idsToFetch.length === 1 ? remoteRepo.retrieveHttpCall(idsToFetch[0]).pipe(map(item => [item])) : remoteRepo.retrieveBulkHttpCall(idsToFetch))
                .pipe(pipeFn, tap(items => this.cacheItems(type, ...items)));

        var cacheRepo = this.getCacheRepository(type);
        if (!cacheRepo) {
            return fetchIds(ids);
        }

        const { attached, stillNeeded } = cacheRepo.claimInFlight(ids);
        const perId = new Map<any, Observable<TEntity>>(attached);
        if (stillNeeded.length > 0) {
            cacheRepo.registerInFlight(stillNeeded, fetchIds(stillNeeded)).forEach((obs$, id) => perId.set(id, obs$));
        }

        // Each id resolves independently once combined - one id's fetch failing (whether it's
        // this call's own fresh fetch or one it happened to attach to) yields nothing for that
        // id rather than failing every id this caller asked for.
        return forkJoin(Array.from(perId.values()).map(obs$ => obs$.pipe(catchError(() => of(<TEntity>null)))))
            .pipe(map(items => items.filter((item): item is TEntity => item != null)));
    }

    public retrieveSingle<TEntity>(withFullHydration: boolean, withFullMapping: boolean, type: new () => TEntity, id: any): Observable<TEntity> {
        if (id === null || id === undefined) return EMPTY;
        return this.retrieve<TEntity>(withFullHydration, withFullMapping, type, id).pipe(map(items => items[0]));
    }

    public getOrRetrieve<TEntity>(withFullHydration: boolean, withFullMapping: boolean, type: new () => TEntity, ...ids: any[]): Observable<TEntity[]> {
        if (ids === null || ids === undefined || ids.length === 0) return of([]);

        ids = ids.filter((item, index, arr) => arr.indexOf(item) === index); //dedupe

        var cacheResult = this.getFromCache(type, ...ids);
        if (!cacheResult || cacheResult.failed?.length > 0) {
            return this.retrieve(withFullHydration, withFullMapping, type, ...cacheResult.failed)
                .pipe(
                    map(items => items.concat(cacheResult.retrieved))
                );
        } else {
            return of(cacheResult.retrieved);
        }
    }

    public cacheItems<TEntity>(type: new () => TEntity, ...items: TEntity[]): TEntity[] {
        if (!items || !items.length) return null;
        items = items.filter(item => !!item);
        if (type === ItemTypeConfig) {
            items = items.filter(item => (<ItemTypeConfig><any>item).hasDefinitionDetails());
        }
        if (!items.length) return null;

        let repo = this.getCacheRepository(type);
        if (repo) {
            repo?.cacheItems(...<Cacheable<any>[]>items);

            items.forEach(item => {
                (<JsonMappable<any>>item).cacheNestedElements(this);
            });
        }
        return items;
    }

    public uncacheItems<TEntity>(type: new () => TEntity, ...items: TEntity[]): TEntity[] {
        if (!items || !items.length) return null;
        let repo = this.getCacheRepository(type);
        if (repo) {
            repo.uncacheItems(...<Cacheable<any>[]>items);
        }
        return items;
    }

    protected mapItems<TEntity>(type: new () => TEntity, ...items: TEntity[]): TEntity[] {
        if (!this.isJsonMappableType(type)) return items;
        return (<JsonMappable<TEntity>[]>items).map((item: JsonMappable<TEntity>) => {
            if (item.isJsonMappableInstance) {
                return <TEntity>item;
            } else {
                return <TEntity>this.jsonMapper.map(item, <any>type);
            }
        });
    }

    protected hydrateItems<TEntity>(type: new () => TEntity, ...items: TEntity[]): Observable<TEntity[]> {
        if (!this.isHydratableType(type)) {
            return of(items);
        }

        var splitByHydration = items.reduce<{ hydrated: TEntity[], dehydrated: TEntity[] }>(
            (accumulator, item) => {
                ((<Hydratable<any>>item).hasBeenRehydrated ? accumulator.hydrated : accumulator.dehydrated).push(item);
                return accumulator;
            },
            { hydrated: [], dehydrated: [] });

        if (splitByHydration.dehydrated.length > 0) {
            return forkJoin(
                splitByHydration.dehydrated.map(item => (<Hydratable<any>>new type()).hydrate(item, this.jsonMapper))
            ).pipe(
                map(items => items.concat(splitByHydration.hydrated))
            );
        }
        return of(items);
    }

    public getFromCache<TIdentifier, TCacheable>(type: new () => TCacheable, ...ids: TIdentifier[]): CacheResult<TIdentifier, TCacheable> {
        let cache = this.getCacheRepository(type);
        if (!cache) {
            return { retrieved: [], failed: ids };
        }

        return <CacheResult<TIdentifier, TCacheable>>cache.getFromCache(...ids);
    }

    public determineId<TIdentifier, TCacheable>(type: new () => TCacheable, jsonShape: any): TIdentifier {
        let repo = this.getCacheRepository(type) ?? this.getRemoteRepository(type);
        return repo?.getId(jsonShape);
    }

    private getRemoteRepository(type: new () => any): AbstractRemoteRepository<any, any> {
        const registration = this.repositoryRegistrations.get(type);
        if (registration?.remoteRepository) {
            return registration.remoteRepository;
        }
        console.error(`Invalid type ${this.getTypeLabel(type)} not supported by URG`);
        return null;
    }

    private getCacheRepository(type: new () => any): CacheRepository<any, any> {
        return this.repositoryRegistrations.get(type)?.cacheRepository ?? null;
    }

    private typeInstance(type: new () => any) {
        return this.repositoryRegistrations.has(type) ? new type() : null;
    }

    private isCacheableType(type: new () => any) {
        return !!this.getCacheRepository(type);
    }
    private isHydratableType(type: new () => any) {
        return (<Hydratable<any>>this.typeInstance(type))?.isHydratableInstance;

    }
    private isJsonMappableType(type: new () => any) {
        return this.typeInstance(type)?.isJsonMappableInstance;
    }

    private getTypeLabel(type: new () => any) {
        return this.typeInstance(type)?.repositoryLabel ?? type?.name ?? 'unknown';
    }
}

interface RepositoryRegistration<TEntity extends Cacheable<TEntity>> {
    remoteRepository?: AbstractRemoteRepository<any, TEntity>;
    cacheRepository?: CacheRepository<any, TEntity>;
}